/*
Welcome screen — 3-card swipeable carousel.

Mirrors Android WelcomeScreen.kt at the structural level (hero +
mechanics + privacy) but ships as three sequential cards instead of
one long scroll. Each card animates in with a scale + fade and a
subtle illustration; the user pages between them via touch swipe,
the dot indicators, the "Suivant / Précédent" buttons, or arrow
keys on desktop.

Two modes, gated by `inRoute.replay`:
- replay=false (first launch): the LAST card's primary action prompts
  for geolocation permission (the rationale is on the screen at the
  moment of consent), marks onboardingDone, and navigates to the map.
- replay=true (entered from Profile): no permission, no flag write —
  the last card's primary action is "Fermer" and pops back.

Implementation notes:
- Three card DOM nodes, laid out side-by-side in a track with
  width:300% transformed by translateX. Live finger-follow during
  touchmove (rubber-band at the boundaries) + snap on release,
  matching the lightbox's gesture model so the whole app feels
  consistent.
- Per-card illustrations are inline SVG (canopée silhouette for the
  hero, a stylised compass dial for the GPS card, a closed-padlock
  shield for the privacy card). All from the same brand palette.
- Dot indicators reflect the current page and are themselves tappable
  shortcuts for users who don't realise the cards swipe.
*/

import { navigate, kPaths, type Route } from '../router'
import { markOnboardingDone } from '../data/onboardingStore'

const kCards = ['hero', 'gps', 'privacy'] as const
type CardId = typeof kCards[number]
const kCardCount = kCards.length

const kSwipeCommitFraction: number = 0.18  // |dx| / track-width past this on release → commit page change
const kSwipeCommitPx: number = 56            // OR |dx| past this → commit (for very wide viewports where the fraction is huge)
const kSwipeAxisLockPx: number = 8           // axis-lock threshold; below this we don't claim the gesture
const kSnapMs: number = 280                  // duration of snap-to-page animation on release
const kEdgeRubberBand: number = 0.4          // attenuation of overscroll past the first/last card

/*
Mount the welcome screen carousel. `inRoute.replay` distinguishes
first-launch (with onboarding flag write + map nav at the end) from
"Comment jouer" replay (back button at the end).
*/
export function mountWelcomeScreen(inRoot: HTMLElement, inRoute: Route): void
{
	const vReplay = inRoute.id === 'welcome' && inRoute.replay === true
	const vFinalLabel = vReplay ? 'Fermer' : 'Commencer'

	inRoot.innerHTML = `
		<div class="screen welcome welcome-carousel">
			<div class="welcome-track" id="welcome-track">
				${kCards.map((inId, inIdx) => renderCard(inId, inIdx, vFinalLabel)).join('')}
			</div>

			<div class="welcome-controls">
				<div class="welcome-dots" role="tablist" aria-label="Étapes">
					${kCards.map((_, inIdx) => `
						<button class="welcome-dot" type="button" role="tab" aria-label="Page ${inIdx + 1}" data-target="${inIdx}"></button>
					`).join('')}
				</div>
				<div class="welcome-nav">
					<button class="welcome-button welcome-button-ghost" id="welcome-prev" type="button" aria-label="Précédent">‹ Précédent</button>
					<button class="welcome-button welcome-button-primary" id="welcome-next" type="button">Suivant ›</button>
				</div>
			</div>
		</div>
	`

	const vTrack = inRoot.querySelector<HTMLElement>('#welcome-track')
	const vPrev = inRoot.querySelector<HTMLButtonElement>('#welcome-prev')
	const vNext = inRoot.querySelector<HTMLButtonElement>('#welcome-next')
	const vDots = Array.from(inRoot.querySelectorAll<HTMLButtonElement>('.welcome-dot'))
	if (!vTrack || !vPrev || !vNext) return

	let vIndex: number = 0  // current card index, 0..kCardCount-1

	const vSetTransform = (inDx: number, inAnimate: boolean): void => {
		// `vDx` is a pixel delta on top of the page-aligned base
		// translate. Track width is 300 % of the carousel width, so
		// each page shift is one carousel width = 100/3 % of the
		// track. translateX accepts mixed units (`calc(% + px)`).
		vTrack.style.transition = inAnimate ? `transform ${kSnapMs}ms cubic-bezier(0.2, 0.8, 0.2, 1)` : 'none'
		vTrack.style.transform = `translateX(calc(${-vIndex * (100 / kCardCount)}% + ${inDx}px))`
	}

	const vRefreshUI = (): void => {
		vDots.forEach((inDot, inIdx) => inDot.setAttribute('aria-selected', inIdx === vIndex ? 'true' : 'false'))
		vPrev.disabled = vIndex === 0
		vPrev.style.visibility = vIndex === 0 ? 'hidden' : 'visible'
		vNext.textContent = vIndex === kCardCount - 1 ? vFinalLabel : 'Suivant ›'
		vNext.classList.toggle('welcome-button-final', vIndex === kCardCount - 1)
		vTrack.querySelectorAll<HTMLElement>('.welcome-card').forEach((inCard, inIdx) => {
			inCard.setAttribute('aria-hidden', inIdx === vIndex ? 'false' : 'true')
			inCard.dataset.active = inIdx === vIndex ? 'true' : 'false'
		})
	}

	const vGoTo = (inIdx: number, inAnimate: boolean = true): void => {
		const vClamped = Math.max(0, Math.min(kCardCount - 1, inIdx))
		vIndex = vClamped
		vSetTransform(0, inAnimate)
		vRefreshUI()
	}

	// Initial paint at index 0 with no transition so the first card
	// just appears in place rather than sliding from off-screen.
	vGoTo(0, false)

	vPrev.addEventListener('click', () => vGoTo(vIndex - 1))
	vNext.addEventListener('click', () => {
		if (vIndex < kCardCount - 1) vGoTo(vIndex + 1)
		else void onFinish(vReplay)
	})
	vDots.forEach((inDot) => inDot.addEventListener('click', () => {
		const vTarget = Number(inDot.dataset.target)
		if (Number.isFinite(vTarget)) vGoTo(vTarget)
	}))

	// Keyboard nav for the desktop replay path.
	document.addEventListener('keydown', onKey)
	function onKey(inEv: KeyboardEvent): void
	{
		if (!document.body.contains(vTrack))
		{
			document.removeEventListener('keydown', onKey)  // self-detach when the screen unmounts
			return
		}
		if (inEv.key === 'ArrowLeft') vGoTo(vIndex - 1)
		else if (inEv.key === 'ArrowRight') vGoTo(vIndex + 1)
		else if (inEv.key === 'Enter' && vIndex === kCardCount - 1) void onFinish(vReplay)
	}

	wireSwipe(vTrack, () => vIndex, (inDx, inAnim) => vSetTransform(inDx, inAnim), vGoTo)
}

/*
Touch swipe handler for the carousel track. Mirrors PhotoLightbox's
gesture model: live follow during touchmove with rubber-band at the
boundaries, snap-or-rewind on release based on the commit threshold.
*/
function wireSwipe(
	inTrack: HTMLElement,
	inGetIndex: () => number,
	inSetTransform: (inDx: number, inAnimate: boolean) => void,
	inGoTo: (inIdx: number, inAnimate?: boolean) => void,
): void
{
	let vTouchStartX: number | null = null  // touch origin x, null when no drag
	let vTouchStartY: number | null = null  // touch origin y, used for axis lock
	let vDragLocked: boolean = false  // true once the gesture is clearly horizontal
	let vDx: number = 0  // running horizontal delta; left swipe → next page is negative

	inTrack.addEventListener('touchstart', (inEv: TouchEvent) => {
		const vT = inEv.touches[0]
		if (!vT) return
		vTouchStartX = vT.clientX
		vTouchStartY = vT.clientY
		vDragLocked = false
		vDx = 0
		inSetTransform(0, false)  // cancel any in-flight snap
	}, { passive: true })

	inTrack.addEventListener('touchmove', (inEv: TouchEvent) => {
		if (vTouchStartX === null || vTouchStartY === null) return
		const vT = inEv.touches[0]
		if (!vT) return
		const vRawDx = vT.clientX - vTouchStartX
		const vRawDy = vT.clientY - vTouchStartY
		if (!vDragLocked)
		{
			if (Math.abs(vRawDx) < kSwipeAxisLockPx && Math.abs(vRawDy) < kSwipeAxisLockPx) return
			if (Math.abs(vRawDy) > Math.abs(vRawDx))
			{
				vTouchStartX = null
				vTouchStartY = null
				return
			}
			vDragLocked = true
		}
		const vIdx = inGetIndex()
		const vAtLeftEdge = vIdx === 0 && vRawDx > 0
		const vAtRightEdge = vIdx === kCardCount - 1 && vRawDx < 0
		vDx = (vAtLeftEdge || vAtRightEdge) ? vRawDx * kEdgeRubberBand : vRawDx
		if (inEv.cancelable) inEv.preventDefault()
		inSetTransform(vDx, false)
	}, { passive: false })

	const vRelease = (): void => {
		if (vTouchStartX === null) { vDx = 0; return }
		const vWidth = inTrack.offsetWidth || 1
		const vCommit = Math.abs(vDx) > kSwipeCommitPx || Math.abs(vDx) / vWidth > kSwipeCommitFraction
		const vIdx = inGetIndex()
		const vDelta = vCommit ? (vDx < 0 ? +1 : -1) : 0
		const vTarget = Math.max(0, Math.min(kCardCount - 1, vIdx + vDelta))
		vTouchStartX = null
		vTouchStartY = null
		vDx = 0
		inGoTo(vTarget, true)
	}
	inTrack.addEventListener('touchend', vRelease)
	inTrack.addEventListener('touchcancel', vRelease)
}

/*
Per-card markup. Each card has its own illustration + headline + body;
the third card carries no inline CTA — the global "Suivant / Commencer"
button at the bottom drives the finish action.
*/
function renderCard(inId: CardId, inIndex: number, inFinalLabel: string): string
{
	if (inId === 'hero')
	{
		return `
			<section class="welcome-card" data-active="${inIndex === 0 ? 'true' : 'false'}" aria-hidden="${inIndex === 0 ? 'false' : 'true'}">
				<div class="welcome-illus welcome-illus-hero" aria-hidden="true">${renderHeroSvg()}</div>
				<h1 class="display welcome-title">CanoPaname <span class="welcome-beta-chip" aria-label="version bêta">BÊTA</span></h1>
				<p class="welcome-tagline">Mission Canopée&nbsp;: la forêt t'appelle…<br>depuis le trottoir.</p>
				<p class="welcome-body">
					213 042 arbres réels, sur la carte de Paris.
					Marche, repère, capture. Construis ton arboretum,
					débloque des badges, traverse les saisons.
				</p>
				<p class="welcome-beta-note">
					⚠️ Version bêta — l'app évolue encore et certaines
					fonctionnalités peuvent changer ou casser. Sauvegarde
					régulièrement tes captures via Profil → Exporter.
				</p>
			</section>
		`
	}
	if (inId === 'gps')
	{
		return `
			<section class="welcome-card" data-active="false" aria-hidden="true">
				<div class="welcome-illus welcome-illus-gps" aria-hidden="true">${renderGpsSvg()}</div>
				<h2 class="display welcome-title">📍 GPS &amp; photo</h2>
				<p class="welcome-body">
					La capture exige un fix GPS récent (moins de 30 secondes,
					moins de 30 mètres) — pour t'attacher à l'arbre que tu
					regardes vraiment. Chaque capture sauvegarde une photo
					locale, qui part dans le ZIP de sauvegarde quand tu le
					déclenches.
				</p>
			</section>
		`
	}
	// privacy
	return `
		<section class="welcome-card" data-active="false" aria-hidden="true">
			<div class="welcome-illus welcome-illus-privacy" aria-hidden="true">${renderPrivacySvg()}</div>
			<h2 class="display welcome-title">🌐 Confidentialité</h2>
			<p class="welcome-body">
				Pas de comptes, pas de classements, pas d'analytics.
				Aucun envoi vers un serveur : ta position, tes photos
				et tes captures restent dans ton appareil.
			</p>
			<p class="welcome-cta-hint">Tape « ${escapeHtml(inFinalLabel)} » ci-dessous pour ${inFinalLabel === 'Fermer' ? 'revenir au profil' : 'autoriser la géoloc et démarrer'}.</p>
		</section>
	`
}

/*
Hero illustration: the same canopée silhouette as the launcher icon,
filled in brand green so the user sees the same shape they tapped on
the home screen. Subtle radial halo for depth.
*/
function renderHeroSvg(): string
{
	return `
		<svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg" role="img">
			<defs>
				<radialGradient id="welcome-hero-halo" cx="50%" cy="46%" r="55%">
					<stop offset="0%" stop-color="#6FAE5A" stop-opacity="0.30"/>
					<stop offset="100%" stop-color="#6FAE5A" stop-opacity="0"/>
				</radialGradient>
			</defs>
			<circle cx="80" cy="74" r="68" fill="url(#welcome-hero-halo)"/>
			<path d="M71.1,133.3 L71.1,97.8 C56.3,97.8 44.4,91.9 38.5,80 C32.6,77 32.6,65.2 35.6,56.3 C32.6,44.4 41.5,32.6 53.3,32.6 C56.3,23.7 68.1,17.8 80,17.8 C91.9,17.8 103.7,23.7 106.7,32.6 C118.5,32.6 127.4,44.4 124.4,56.3 C127.4,65.2 127.4,77 121.5,80 C115.6,91.9 103.7,97.8 88.9,97.8 L88.9,133.3 Z" fill="#2E5D3A"/>
		</svg>
	`
}

/*
GPS illustration: a stylised compass dial with a north-pointing
needle, plus a 30 m proximity ring around it (mirrors the actual
capture gate the body text talks about).
*/
function renderGpsSvg(): string
{
	return `
		<svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg" role="img">
			<circle cx="80" cy="80" r="68" fill="#E8F0DD"/>
			<circle cx="80" cy="80" r="48" fill="none" stroke="#2E5D3A" stroke-width="2" stroke-dasharray="2 4"/>
			<circle cx="80" cy="80" r="22" fill="#2E5D3A"/>
			<path d="M80 60 L72 90 L80 86 L88 90 Z" fill="#F5F1E6"/>
			<circle cx="80" cy="80" r="3" fill="#F5F1E6"/>
			<text x="80" y="32" fill="#2E5D3A" font-family="Georgia, serif" font-weight="600" font-size="11" text-anchor="middle">N</text>
		</svg>
	`
}

/*
Privacy illustration: a closed padlock against a shield, in muted
ecorce + cream. No labels — relying on the universal padlock metaphor
keeps it readable across locales.
*/
function renderPrivacySvg(): string
{
	return `
		<svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg" role="img">
			<path d="M80 18 L132 36 L132 86 C132 112 110 134 80 142 C50 134 28 112 28 86 L28 36 Z" fill="#2E5D3A"/>
			<path d="M80 26 L124 41 L124 86 C124 108 105 127 80 134 C55 127 36 108 36 86 L36 41 Z" fill="#6FAE5A" fill-opacity="0.25"/>
			<rect x="60" y="76" width="40" height="34" rx="6" fill="#F5F1E6"/>
			<path d="M68 76 L68 66 C68 58 73 53 80 53 C87 53 92 58 92 66 L92 76" fill="none" stroke="#F5F1E6" stroke-width="6" stroke-linecap="round"/>
			<circle cx="80" cy="91" r="4.5" fill="#2E5D3A"/>
			<rect x="78" y="91" width="4" height="9" rx="1.5" fill="#2E5D3A"/>
		</svg>
	`
}

/*
Final-action handler — fires when the user taps the rightmost button
on the privacy card.
- Replay mode: just pop back (or fall back to the profile screen if
  there's no history entry to pop to).
- First-launch: fire-and-forget the geolocation prompt (the user
  can deny without blocking onboarding), mark onboardingDone, and
  navigate to the map. Any of these can fail without dragging the
  whole flow down — soft logs only.
*/
async function onFinish(inReplay: boolean): Promise<void>
{
	if (inReplay)
	{
		if (history.length > 1) history.back()
		else navigate(kPaths.profile())
		return
	}

	requestGeolocationPermission()  // not awaited — we let it run in parallel with the map nav
	try
	{
		await markOnboardingDone()
	}
	catch (vErr)
	{
		console.warn('Onboarding flag write failed', vErr)
	}
	navigate(kPaths.map())
}

/*
Trigger the browser's geolocation prompt. Side-effect only — we just
want the OS dialog to appear while the rationale is on screen. The
response payload is discarded; the capture FAB will re-prompt later
if the user denied.
*/
function requestGeolocationPermission(): void
{
	if (!('geolocation' in navigator)) return
	navigator.geolocation.getCurrentPosition(
		() => { /* nothing to do — we just wanted the prompt to fire */ },
		() => { /* user can deny; the capture FAB will re-prompt later */ },
		{ enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
	)
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
